import { useState, useEffect } from 'react';
import { 
    Building2, 
    Plus, 
    Search,
    Phone,
    Mail,
    Clock,
    TrendingUp,
    AlertTriangle,
    MoreHorizontal,
    Pencil
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BrokersPage() {
    const [brokers, setBrokers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newBroker, setNewBroker] = useState({
        name: '',
        mc_number: '',
        contact_email: '',
        contact_phone: '',
        days_to_pay: '',
        credit_score: ''
    });
    
    // Edit broker state
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingBroker, setEditingBroker] = useState(null);
    const [editForm, setEditForm] = useState({ credit_score: '', days_to_pay: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchBrokers();
    }, []);

    const fetchBrokers = async () => {
        try {
            const res = await fetch(`${API_URL}/api/brokers`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setBrokers(data);
            }
        } catch (error) {
            console.error('Failed to fetch brokers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddBroker = async () => {
        try {
            const res = await fetch(`${API_URL}/api/brokers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    ...newBroker,
                    days_to_pay: newBroker.days_to_pay ? parseInt(newBroker.days_to_pay) : null,
                    credit_score: newBroker.credit_score ? parseInt(newBroker.credit_score) : null
                })
            });
            if (res.ok) {
                setIsAddDialogOpen(false);
                setNewBroker({ name: '', mc_number: '', contact_email: '', contact_phone: '', days_to_pay: '', credit_score: '' });
                fetchBrokers();
            }
        } catch (error) {
            console.error('Failed to add broker:', error);
        }
    };

    const getPerformanceScoreColor = (score) => {
        if (score === null || score === undefined) return 'bg-zinc-500';
        if (score >= 85) return 'bg-green-600';
        if (score >= 70) return 'bg-amber-500';
        if (score >= 50) return 'bg-orange-500';
        return 'bg-red-600';
    };

    const openEditDialog = (broker) => {
        setEditingBroker(broker);
        setEditForm({
            credit_score: broker.credit_score?.toString() || '',
            days_to_pay: broker.days_to_pay?.toString() || ''
        });
        setIsEditDialogOpen(true);
    };

    const handleEditBroker = async () => {
        if (!editingBroker) return;
        
        setSaving(true);
        try {
            const updateData = {};
            if (editForm.credit_score !== '') {
                updateData.credit_score = parseInt(editForm.credit_score);
            }
            if (editForm.days_to_pay !== '') {
                updateData.days_to_pay = parseInt(editForm.days_to_pay);
            }
            
            const res = await fetch(`${API_URL}/api/brokers/${editingBroker.broker_id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(updateData)
            });
            
            if (res.ok) {
                toast.success('Broker updated successfully');
                setIsEditDialogOpen(false);
                setEditingBroker(null);
                fetchBrokers();
            } else {
                const err = await res.json();
                toast.error('Failed to update broker', { description: err.detail || 'Unknown error' });
            }
        } catch (error) {
            console.error('Failed to update broker:', error);
            toast.error('Failed to update broker', { description: error.message });
        } finally {
            setSaving(false);
        }
    };

    const getPerformanceScoreDisplay = (score) => {
        if (score === null || score === undefined) return '—';
        return Math.round(score);
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-500/20 text-green-400 border-green-500/30';
            case 'inactive': return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
            case 'blacklisted': return 'bg-red-500/20 text-red-400 border-red-500/30';
            default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const filteredBrokers = brokers.filter(broker => 
        broker.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        broker.mc_number?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="brokers-page">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">Brokers</h1>
                    <p className="text-zinc-500 text-sm mt-1">Manage broker relationships and track performance</p>
                </div>
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-broker-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Broker
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-800">
                        <DialogHeader>
                            <DialogTitle className="text-white">Add New Broker</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            <div>
                                <Label className="text-zinc-400">Broker Name *</Label>
                                <Input 
                                    value={newBroker.name}
                                    onChange={(e) => setNewBroker({...newBroker, name: e.target.value})}
                                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                    placeholder="TQL Logistics"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400">MC Number</Label>
                                    <Input 
                                        value={newBroker.mc_number}
                                        onChange={(e) => setNewBroker({...newBroker, mc_number: e.target.value})}
                                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                        placeholder="MC123456"
                                    />
                                </div>
                                <div>
                                    <Label className="text-zinc-400">Credit Score</Label>
                                    <Input 
                                        type="number"
                                        value={newBroker.credit_score}
                                        onChange={(e) => setNewBroker({...newBroker, credit_score: e.target.value})}
                                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                        placeholder="95"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400">Email</Label>
                                    <Input 
                                        type="email"
                                        value={newBroker.contact_email}
                                        onChange={(e) => setNewBroker({...newBroker, contact_email: e.target.value})}
                                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                        placeholder="dispatch@broker.com"
                                    />
                                </div>
                                <div>
                                    <Label className="text-zinc-400">Phone</Label>
                                    <Input 
                                        value={newBroker.contact_phone}
                                        onChange={(e) => setNewBroker({...newBroker, contact_phone: e.target.value})}
                                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                        placeholder="(555) 123-4567"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400">Days to Pay</Label>
                                <Input 
                                    type="number"
                                    value={newBroker.days_to_pay}
                                    onChange={(e) => setNewBroker({...newBroker, days_to_pay: e.target.value})}
                                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                    placeholder="30"
                                />
                            </div>
                            <Button 
                                onClick={handleAddBroker}
                                disabled={!newBroker.name}
                                className="w-full bg-orange-500 hover:bg-orange-600"
                            >
                                Add Broker
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Search Bar */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500 w-4 h-4" />
                <Input 
                    placeholder="Search by name or MC number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-zinc-900 border-zinc-800 text-white"
                    data-testid="broker-search-input"
                />
            </div>

            {/* Brokers Grid */}
            <TooltipProvider>
                {filteredBrokers.length === 0 ? (
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardContent className="py-12 text-center">
                            <Building2 className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                            <p className="text-zinc-500">No brokers found</p>
                            <Button 
                                variant="outline" 
                                className="mt-4 border-zinc-700"
                                onClick={() => setIsAddDialogOpen(true)}
                            >
                                Add Your First Broker
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredBrokers.map((broker) => (
                            <Card 
                                key={broker.broker_id} 
                                className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
                                data-testid={`broker-card-${broker.broker_id}`}
                            >
                                <CardContent className="p-5">
                                    {/* Header with Name and Score */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3">
                                                <h3 className="text-lg font-semibold text-white truncate">
                                                    {broker.name}
                                                </h3>
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <div 
                                                            className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold ${getPerformanceScoreColor(broker.performance_score)}`}
                                                            data-testid={`broker-score-${broker.broker_id}`}
                                                        >
                                                            {getPerformanceScoreDisplay(broker.performance_score)}
                                                        </div>
                                                    </TooltipTrigger>
                                                    <TooltipContent className="bg-zinc-800 border-zinc-700 text-zinc-200 max-w-xs">
                                                        <p>Performance score based on payment speed, disputes, and reliability.</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </div>
                                            {broker.mc_number && (
                                                <p className="text-zinc-500 text-sm mt-1">MC# {broker.mc_number}</p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge className={`${getStatusBadgeColor(broker.status)} text-xs uppercase`}>
                                                {broker.status}
                                            </Badge>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800"
                                                        data-testid={`broker-menu-${broker.broker_id}`}
                                                    >
                                                        <MoreHorizontal className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                                                    <DropdownMenuItem 
                                                        onClick={() => openEditDialog(broker)}
                                                        className="text-zinc-300 focus:bg-zinc-800 focus:text-white cursor-pointer"
                                                        data-testid={`edit-broker-${broker.broker_id}`}
                                                    >
                                                        <Pencil className="w-4 h-4 mr-2" />
                                                        Edit
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </div>

                                    {/* Contact Info */}
                                    <div className="space-y-2 mb-4">
                                        {broker.contact_email && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <Mail className="w-4 h-4 text-zinc-500" />
                                                <span className="text-zinc-400 truncate">{broker.contact_email}</span>
                                            </div>
                                        )}
                                        {broker.contact_phone && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <Phone className="w-4 h-4 text-zinc-500" />
                                                <span className="text-zinc-400">{broker.contact_phone}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Stats */}
                                    <div className="grid grid-cols-2 gap-3 pt-4 border-t border-zinc-800">
                                        <div>
                                            <p className="text-xs text-zinc-500 uppercase">Loads</p>
                                            <p className="text-lg font-mono text-white">{broker.total_loads_booked || 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-zinc-500 uppercase">Revenue</p>
                                            <p className="text-lg font-mono text-green-400">{formatCurrency(broker.total_revenue_generated)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-zinc-500 uppercase">Avg Days to Pay</p>
                                            <p className="text-lg font-mono text-white">
                                                {broker.average_days_to_pay ? `${Math.round(broker.average_days_to_pay)}d` : '—'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-zinc-500 uppercase">Credit Score</p>
                                            <p className="text-lg font-mono text-white">{broker.credit_score || '—'}</p>
                                        </div>
                                    </div>

                                    {/* Warnings */}
                                    {(broker.late_payment_count > 0 || broker.discrepancy_count > 0) && (
                                        <div className="mt-4 pt-4 border-t border-zinc-800">
                                            <div className="flex items-center gap-4 text-sm">
                                                {broker.late_payment_count > 0 && (
                                                    <div className="flex items-center gap-1 text-amber-500">
                                                        <Clock className="w-4 h-4" />
                                                        <span>{broker.late_payment_count} late</span>
                                                    </div>
                                                )}
                                                {broker.discrepancy_count > 0 && (
                                                    <div className="flex items-center gap-1 text-red-500">
                                                        <AlertTriangle className="w-4 h-4" />
                                                        <span>{broker.discrepancy_count} disputes</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </TooltipProvider>
            
            {/* Edit Broker Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold">
                            Edit Broker
                        </DialogTitle>
                        {editingBroker && (
                            <p className="text-zinc-400 text-sm">{editingBroker.name}</p>
                        )}
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label className="text-zinc-400">Credit Score</Label>
                            <Input 
                                type="number"
                                min="0"
                                max="100"
                                value={editForm.credit_score}
                                onChange={(e) => setEditForm({...editForm, credit_score: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                placeholder="0-100"
                                data-testid="edit-broker-credit-score"
                            />
                            <p className="text-xs text-zinc-500 mt-1">Score from 0-100 indicating broker reliability</p>
                        </div>
                        <div>
                            <Label className="text-zinc-400">Days to Pay</Label>
                            <Input 
                                type="number"
                                min="0"
                                value={editForm.days_to_pay}
                                onChange={(e) => setEditForm({...editForm, days_to_pay: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                placeholder="30"
                                data-testid="edit-broker-days-to-pay"
                            />
                            <p className="text-xs text-zinc-500 mt-1">Average number of days broker takes to pay</p>
                        </div>
                    </div>
                    <DialogFooter className="mt-6">
                        <Button 
                            variant="outline" 
                            onClick={() => setIsEditDialogOpen(false)}
                            className="border-zinc-700 text-zinc-300"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleEditBroker}
                            disabled={saving}
                            className="bg-orange-500 hover:bg-orange-600"
                            data-testid="save-broker-btn"
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
